// pages/PrivacyPage.tsx - Privacy policy with translations
import React from 'react';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPage: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-cyan-900 via-blue-900 to-teal-900 py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 text-center">
            {t('privacy.title')}
          </h1>
          <p className="text-xl text-cyan-100 max-w-3xl mx-auto text-center">
            {t('privacy.subtitle')}
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 via-gray-50/50 to-transparent pointer-events-none" />
      </div>

      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section1.title')}</h2>
              <p className="text-gray-700 mb-2"><strong>FennoFuture</strong></p>
              <p className="text-gray-700">{t('privacy.section1.businessId')}: 3203489-7</p>
              <p className="text-gray-700">Turuntie 3, 37830 Akaa</p>
              <p className="text-gray-700">{t('privacy.section1.email')}: innovation@fennofuture.fi</p>
              <p className="text-gray-700">{t('privacy.section1.phone')}: +358 400 416 466</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section2.title')}</h2>
              <p className="text-gray-700">{t('privacy.section2.text')}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section3.title')}</h2>
              <p className="text-gray-700 mb-3">{t('privacy.section3.intro')}</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('privacy.section3.item1')}</li>
                <li>{t('privacy.section3.item2')}</li>
                <li>{t('privacy.section3.item3')}</li>
                <li>{t('privacy.section3.item4')}</li>
                <li>{t('privacy.section3.item5')}</li>
                <li>{t('privacy.section3.item6')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section4.title')}</h2>
              <p className="text-gray-700 mb-3">{t('privacy.section4.intro')}</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('privacy.section4.item1')}</li>
                <li>{t('privacy.section4.item2')}</li>
                <li>{t('privacy.section4.item3')}</li>
                <li>{t('privacy.section4.item4')}</li>
                <li>{t('privacy.section4.item5')}</li>
                <li>{t('privacy.section4.item6')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section5.title')}</h2>
              <p className="text-gray-700">
                {t('privacy.section5.text')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section6.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('privacy.section6.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('privacy.section6.item1')}</li>
                <li>{t('privacy.section6.item2')}</li>
                <li>{t('privacy.section6.item3')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section7.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('privacy.section7.intro')}
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('privacy.section7.access.title')}</h3>
                  <p className="text-gray-700 text-sm">
                    {t('privacy.section7.access.text')}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('privacy.section7.rectification.title')}</h3>
                  <p className="text-gray-700 text-sm">
                    {t('privacy.section7.rectification.text')}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('privacy.section7.erasure.title')}</h3>
                  <p className="text-gray-700 text-sm mb-2">
                    {t('privacy.section7.erasure.text1')}
                  </p>
                  <p className="text-gray-700 text-sm">
                    {t('privacy.section7.erasure.text2')}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('privacy.section7.restriction.title')}</h3>
                  <p className="text-gray-700 text-sm">
                    {t('privacy.section7.restriction.text')}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('privacy.section7.objection.title')}</h3>
                  <p className="text-gray-700 text-sm">
                    {t('privacy.section7.objection.text')}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('privacy.section7.portability.title')}</h3>
                  <p className="text-gray-700 text-sm">
                    {t('privacy.section7.portability.text')}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('privacy.section7.withdraw.title')}</h3>
                  <p className="text-gray-700 text-sm">
                    {t('privacy.section7.withdraw.text')}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('privacy.section7.complaint.title')}</h3>
                  <p className="text-gray-700 text-sm">
                    {t('privacy.section7.complaint.text')}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <p className="text-gray-700 font-semibold mb-2">{t('privacy.section7.exercise.title')}</p>
                <p className="text-gray-700 text-sm mb-2">
                  {t('privacy.section7.exercise.intro')}
                </p>
                <p className="text-gray-700 text-sm">
                  {t('privacy.section7.exercise.email')}: <a href="mailto:info@potkurikorjaus.fi" className="text-cyan-600 hover:underline">info@potkurikorjaus.fi</a><br />
                  {t('privacy.section7.exercise.phone')}: <a href="tel:+358400416466" className="text-cyan-600 hover:underline">+358 400 416 466</a><br />
                  {t('privacy.section7.exercise.address')}
                </p>
                <p className="text-gray-700 text-sm mt-2">
                  {t('privacy.section7.exercise.response')}
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section8.title')}</h2>
              <p className="text-gray-700">
                {t('privacy.section8.text')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section9.title')}</h2>
              <p className="text-gray-700">
                {t('privacy.section9.text')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('privacy.section10.title')}</h2>
              <p className="text-gray-700">
                {t('privacy.section10.text')}
              </p>
            </section>

            <section className="border-t pt-6">
              <p className="text-sm text-gray-600">
                <strong>{t('privacy.updated')}:</strong> 3.1.2026
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
